'use client';
import React, { useEffect, useState } from 'react';
import * as blockSections from '../block-sections';
import { FallbackBlock } from '../fallback-block';
import dynamic from 'next/dynamic';
import IkeaProductCardInHomePage from '@/components/page-components/home-page/ikea-product-card';
import { IContentfulImage } from '@/interfaces/contentful-image.interface';
import { ILibraryOpeningHours } from '@/lib/fragments/libraryOpeningHours';
import { ILibraryOpeningHoursException } from '@/lib/fragments/libraryOpeningHoursException';
import { ILibraryMeetingPlaceDictionary } from '@/lib/fragments/libraryMeetingPlaceDictionary';
import { TextArrayProps } from '@/components/page-components/editorial-page-full-width/editorial-page-full-width';
import { useIsMobile } from '@/hooks/use-is-mobile';
import {
  clearUrlParams,
  getLocalStorageItem,
  getUserAgent,
  PWA_GUIDE_CLOSED_EVENT,
  setLocalStorageItem,
} from '@/helpers/utils';
import Backdrop from '@/components/ui/backdrop/backdrop';
import { SideModal } from '@/components/ui/side-modal/side-modal';
import { isBrowser } from '@/helpers/is-browser';
import { SideModalBody } from '@/components/ui/side-modal/side-modal-body';
import { SafariGuide } from '@/components/pwa-banner/safari-guide';
import CloseIcon from '@/assets/icons/close.svg';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';
import { CardEvent } from '@/components/cards/card-event';
import { getLatestLFEvent } from '@/helpers/api/livli-family';
import LivliFamilyCardEventWrapper from '@/components/page-components/livli-family-homepage/livli-family-homepage-event-card';
import MemberDayTile from '@/components/blocks/member-day-tile/member-day-tile';
import { findMemberDayEditorialCard } from '@/lib/helpers/findMemberDayEditorialCard';

import { ITextAndImageCardBlock } from '@/lib/fragments/textAndImageCardBlock';
import LivliFamilyCardOfferWrapper from '@/components/page-components/livli-family-homepage/home-page-offer-card';
import { CardIkeaFamilyBenefit } from '@/components/cards/card-ikea-family-benefit';
import type { IkeaFamilyBenefitItem } from '@/lib/getIkeaFamilyBenefits';

// Dynamically import Masonry only on client
const ResponsiveMasonry = dynamic(
  () => import('react-responsive-masonry').then((mod) => mod.ResponsiveMasonry),
  { ssr: false },
);
const Masonry = dynamic(() => import('react-responsive-masonry'), {
  ssr: false,
});

interface RenderBlocksProps {
  blocks: blockSections.BlockSections[];
  meetingPlaceName?: string;
  integrationId?: string;
  businessUnitCode?: string;
  header?: string;
  logo?: IContentfulImage;
  node_locale?: string;
  primaryLanguage?: string;
  topBlock?: boolean;
  textItem?: TextArrayProps[];
  masonry?: boolean;
  dictionary?: ILibraryMeetingPlaceDictionary;
  showIkeaProductCard?: boolean;
  meetingPlaceOpeningHours?: {
    openingHours?: ILibraryOpeningHours;
    openingHoursExceptions?: ILibraryOpeningHoursException[];
    timezone?: string;
  };
  tenantOpeningHoursDetails?: {
    name: string;
    openingHours: ILibraryOpeningHours;
    openingHoursExceptions: ILibraryOpeningHoursException[];
    timezone: string;
    slug: string;
  }[];
  htmlTitleSuffix?: string;
  isChinaPage?: boolean;
  isLivliFamily?: boolean;
  timezone?: string;
  cardList?: ITextAndImageCardBlock[];
  preview?: boolean;
  ikeaFamilyBenefit?: IkeaFamilyBenefitItem | null;
}
export const RenderBlocks = ({
  blocks,
  logo,
  meetingPlaceName,
  integrationId,
  businessUnitCode,
  header,
  node_locale,
  primaryLanguage,
  topBlock,
  textItem,
  masonry = false,
  dictionary,
  showIkeaProductCard = false,
  meetingPlaceOpeningHours,
  tenantOpeningHoursDetails,
  htmlTitleSuffix,
  isChinaPage = false,
  isLivliFamily = false,
  timezone,
  cardList = [],
  preview = false,
  ikeaFamilyBenefit,
}: RenderBlocksProps) => {
  type BlockType = keyof typeof blockSections;

  const constantDictionary = useGetLibraryConstantsDictionaryData();
  const config = useTenantConfig();
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPWAMobileViewOpen, setIsPWAMobileViewOpen] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [isGuideClosed, setIsGuideClosed] = useState(false);

  let memberDaySlug: string | null = null;
  let memberDayCardText: string | null = null;
  if (Array.isArray(cardList)) {
    // finding editorial card with livliFamilyMemberOffer tag
    const editorialCard = findMemberDayEditorialCard(cardList as any);
    if (editorialCard) {
      memberDaySlug = editorialCard.cardLink?.slug || null;
      memberDayCardText = editorialCard.title || null;
    }
  }

  const isMobile = useIsMobile();

  // Detect device type and browser on mount
  useEffect(() => {
    const ua = getUserAgent();
    if (ua) {
      const osName = ua.os?.name;
      const browserName = ua.browser?.name;

      setIsIOS(osName === 'iOS');

      // Check if browser is Safari (doesn't support beforeinstallprompt)
      const isSafariBrowser =
        browserName === 'Safari' || browserName === 'Mobile Safari';
      setIsSafari(isSafariBrowser);
    }

    // Detect if the PWA is already installed. Standalone mode covers the app
    // launched from the home-screen icon (incl. iOS), while the persisted flag
    // keeps the tile hidden on later visits in a normal browser tab.
    if (isBrowser) {
      const isInStandaloneMode =
        window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true;
      const bannerInstalled =
        getLocalStorageItem('pwaBanner-installed') === 'true';
      setIsPWAInstalled(isInStandaloneMode || bannerInstalled);
      setIsGuideClosed(getLocalStorageItem('pwaGuideClosed') === 'true');
    }
  }, []);

  // Handle backdrop class for mobile view
  useEffect(() => {
    isPWAMobileViewOpen
      ? document.body.classList.add('backdrop')
      : document.body.classList.remove('backdrop');
  }, [isPWAMobileViewOpen]);

  // Keep the tile in sync with the banner: if the Safari guide is closed from
  // the banner (or another tile instance), hide this tile immediately too.
  useEffect(() => {
    if (!isBrowser) return;

    const handleGuideClosed = () => setIsGuideClosed(true);
    window.addEventListener(PWA_GUIDE_CLOSED_EVENT, handleGuideClosed);

    return () => {
      window.removeEventListener(PWA_GUIDE_CLOSED_EVENT, handleGuideClosed);
    };
  }, []);

  const handleClick = async () => {
    // For iOS, show the Safari guide since beforeinstallprompt is not supported
    if (isIOS || isSafari) {
      if (isMobile) {
        setIsPWAMobileViewOpen(true);
      } else {
        setIsSidebarOpen(true);
      }
      return;
    }
    // Get the deferred prompt from global scope (captured in layout.tsx)
    const deferredPrompt = (window as any).__PWA_DEFERRED_PROMPT__;

    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt();

        // Wait for the user's response
        const choice = await deferredPrompt.userChoice;

        if (choice.outcome === 'accepted') {
          console.log('PWA installed successfully');
        } else {
          console.log('PWA installation dismissed');
        }

        // Clear the deferred prompt - it can only be used once
        (window as any).__PWA_DEFERRED_PROMPT__ = null;
      } catch (error) {
        console.error('Error showing install prompt:', error);
      }
    } else {
      console.log('PWA install prompt not available');
    }
  };

  // Closing the Safari install guide dismisses the PWA prompt for good: persist
  // a flag and broadcast an event so both the homepage tile and the banner hide
  // immediately, regardless of which surface opened the guide.
  const handleGuideClose = () => {
    setLocalStorageItem('pwaGuideClosed', 'true');
    setIsGuideClosed(true);
    setIsSidebarOpen(false);
    setIsPWAMobileViewOpen(false);
    if (isBrowser) {
      clearUrlParams();
      window.dispatchEvent(new Event(PWA_GUIDE_CLOSED_EVENT));
    }
  };

  if (masonry) {
    return (
      <>
        <ResponsiveMasonry columnsCountBreakPoints={{ 0: 1, 640: 2 }}>
          <Masonry
            columnsCount={2}
            itemStyle={{ gap: '10px' }}
            style={{ gap: '1.25rem' }}
          >
            {!isChinaPage &&
              isLivliFamily &&
              businessUnitCode &&
              node_locale && (
                <MemberDayTile
                  businessUnitCode={businessUnitCode}
                  nodeLocale={node_locale}
                  timezone={timezone}
                  cardList={cardList}
                  memberDaySlug={memberDaySlug}
                  memberDayCardText={memberDayCardText}
                />
              )}
            {isLivliFamily && !isMobile && (
              <div className="w-full">
                <LivliFamilyCardOfferWrapper
                  businessUnitCode={businessUnitCode}
                  node_locale={node_locale || ''}
                  dictionary={dictionary}
                  cardIndex={1}
                />
              </div>
            )}
            {showIkeaProductCard && (
              <IkeaProductCardInHomePage
                node_locale={node_locale || ''}
                primaryLanguage={primaryLanguage || ''}
                businessUnitCode={businessUnitCode || ''}
                buyNowText={dictionary?.buyAtIkea || ''}
                variant="homepage"
                pageName={
                  htmlTitleSuffix
                    ? meetingPlaceName + htmlTitleSuffix
                    : meetingPlaceName
                      ? meetingPlaceName
                      : ''
                }
              />
            )}
            {ikeaFamilyBenefit && !isMobile && (
              <div className="w-full">
                <CardIkeaFamilyBenefit
                  card={ikeaFamilyBenefit}
                  listType="offer"
                  fixedHeight
                  pageName={
                    htmlTitleSuffix
                      ? meetingPlaceName + htmlTitleSuffix
                      : meetingPlaceName
                        ? meetingPlaceName
                        : ''
                  }
                />
              </div>
            )}
            {isLivliFamily && !isMobile && (
              <div className="w-full">
                <LivliFamilyCardEventWrapper
                  businessUnitCode={businessUnitCode}
                  node_locale={node_locale || ''}
                  dictionary={dictionary}
                  cardIndex={1}
                  isLivliFamily={isLivliFamily}
                />
              </div>
            )}
            {isLivliFamily && isMobile && (
              <div className="w-full">
                <LivliFamilyCardEventWrapper
                  businessUnitCode={businessUnitCode}
                  node_locale={node_locale || ''}
                  dictionary={dictionary}
                  cardIndex={1}
                  isLivliFamily={isLivliFamily}
                />
              </div>
            )}
            {isLivliFamily && isMobile && (
              <div className="w-full">
                <LivliFamilyCardOfferWrapper
                  businessUnitCode={businessUnitCode}
                  node_locale={node_locale || ''}
                  dictionary={dictionary}
                  cardIndex={1}
                />
              </div>
            )}
            {ikeaFamilyBenefit && isMobile && (
              <div className="w-full">
                <CardIkeaFamilyBenefit
                  card={ikeaFamilyBenefit}
                  listType="offer"
                  fixedHeight
                  pageName={
                    htmlTitleSuffix
                      ? meetingPlaceName + htmlTitleSuffix
                      : meetingPlaceName
                        ? meetingPlaceName
                        : ''
                  }
                />
              </div>
            )}
            {blocks?.filter(Boolean)?.map((block: any, index: any) => {
              const newBlockProps = {
                ...block,
                logo,
                meetingPlaceName,
                integrationId,
                header,
                index,
                node_locale,
              };
              const Component =
                blockSections[block?.blocktype as BlockType] || FallbackBlock;
              return (
                <Component
                  key={`${index}-${block.id}`}
                  topBlock={topBlock ? topBlock : null}
                  textItem={textItem ? textItem : null}
                  {...(newBlockProps as any)}
                />
              );
            })}
            {!isChinaPage &&
              isLivliFamily &&
              !isPWAInstalled &&
              !isGuideClosed && (
                <div
                  className="bg-brand-yellow-1 mb-5 text-headingm bg-dynamicStrokeYellow font-title text-neutral-grey-700 h-[200px] justify-center bg-cover flex animate-fadeIn  items-center cursor-pointer flex-col rounded-xl bg-background-primary-light p-6 transition-all duration-200 lg:hover:brightness-90 focus:outline focus:outline-3 focus:outline-offset-3 focus:outline-text-light animation-delay"
                  onClick={handleClick}
                >
                  {constantDictionary?.pwaBlockBanner}
                </div>
              )}
          </Masonry>
        </ResponsiveMasonry>
        {/* Safari Guide SideModal for iOS Desktop */}
        {!isMobile && isSidebarOpen && (
          <SideModal isOpen={isSidebarOpen} handleClose={handleGuideClose}>
            <SideModalBody>
              <SafariGuide
                node_locale={node_locale || ''}
                meetingPlaceName={meetingPlaceName ?? ''}
              />
            </SideModalBody>
          </SideModal>
        )}

        {/* Safari Guide Backdrop for iOS Mobile */}
        {isPWAMobileViewOpen && isMobile && (
          <Backdrop
            handleOpen={handleGuideClose}
            showButtons={false}
            className="!min-h-[250px]"
          >
            <>
              <SafariGuide
                node_locale={node_locale || ''}
                isMobile={true}
                handleClose={handleGuideClose}
                meetingPlaceName={meetingPlaceName ?? ''}
              />
              <button
                className="absolute -top-20 right-4 z-[200] grid h-12 w-12 place-items-center rounded-full bg-brand-grey-1 outline-0 transition-all focus:bg-brand-grey-2 focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-4 focus-visible:outline-brand-grey-2 active:bg-brand-grey-2 lg:hidden lg:h-15 lg:w-15 lg:bg-brand-yellow-1 lg:hover:bg-accent-primary-dark lg:focus:bg-accent-primary-dark lg:active:bg-accent-primary-dark"
                onClick={handleGuideClose}
              >
                <CloseIcon />
              </button>
            </>
          </Backdrop>
        )}
      </>
    );
  } else {
    return (
      <>
        {blocks?.filter(Boolean)?.map((block: any, index: any) => {
          const newBlockProps = {
            ...block,
            logo,
            meetingPlaceName,
            integrationId,
            businessUnitCode,
            timezone,
            header,
            index,
            node_locale,
            meetingPlaceOpeningHours,
            tenantOpeningHoursDetails,
          };
          const Component =
            blockSections[block?.blocktype as BlockType] || FallbackBlock;
          return (
            <Component
              key={`${index}-${block.id}`}
              topBlock={topBlock ? topBlock : null}
              textItem={textItem ? textItem : null}
              {...(newBlockProps as any)}
            />
          );
        })}
      </>
    );
  }
};
